/**
 * FigureEditContext — 편집기 안 그림에 「편집 버튼」을 켜는 스위치.
 *
 * 계약: docs/exam-builder-v2/BE-20260827-figure-editing-apis.md
 * (그림 편집·재요청·이력 API. 대상은 OCR 검수 `/api/v2/ocr/problems/{id}` 와
 *  문제은행 `/api/v2/problems/{id}` 둘이고 경로만 다르다.)
 *
 * ── 왜 context 인가 ──
 * 버튼을 다는 자리는 ImageComponent 다. 거기가 이미 「선택됐는가(isSelected)」와
 * 「고칠 수 있는 화면인가(useLexicalEditable)」를 둘 다 알고 있어서, 오너 지시
 * (「그림을 선택하면 버튼이 뜬다 · 보기 모드에는 넣지 않는다」)가 그 두 값의 곱으로 그대로 나온다.
 *
 * 문제는 파급이다. `src/editor` 는 공용이고 소비자가 13곳인데 그중에는
 * v1 유물 편집기 6곳과 **인쇄 미리보기**(components/TestPapers/PrintModal.js:262 —
 * `<Editor />` 에 isEditable 을 안 줘 기본값 true 다)가 섞여 있다. isEditable 만으로 켜면
 * 인쇄 미리보기에 편집 버튼이 뜬다.
 *
 * 그래서 **Provider 가 없으면 버튼도 없다**. 켤 화면만 Provider 로 감싼다.
 * 같은 수법을 이 저장소가 이미 쓴다 — BlockRenderer 의 PageImageContext.
 * Lexical 0.11 의 데코레이터는 RichTextPlugin 안에서 도므로 React context 가 그대로 닿는다.
 *
 * ── 왜 field 를 여기 싣나 ──
 * 본문 편집기와 풀이 편집기는 각자 LexicalComposer 라 **인스턴스도 root 도 완전히 별개**이고,
 * 그림 순번(index)이 각각 0부터 따로 매겨진다 — 계약의 `field: body|solution` 과 1:1 이다.
 * 선택에서 field 를 유도하면 풀이 그림 0번에 `figures/body/0` 이 나가 **본문 첫 그림이 덮인다.**
 * field 는 Provider 가 박아 주는 상수여야 한다.
 *
 * ── 여기 싣지 않는 것 ──
 * problem_id · 대상(ocr/bank) · 토큰 · getAccessToken. 전부 호출부가 쥔다.
 * `src/editor` 가 v2 API 를 알게 되면 공용 편집기가 이 앱의 계약에 묶인다.
 */
import { createContext, useContext } from 'react';

/**
 * value 모양:
 * {
 *   field: 'body' | 'solution',
 *   onAction?: ({ action, field, index, src, altText, nodeKey }) => boolean | void,
 *   onPasteImage?: ({ field, src, altText, precedingSrcs }) => void,
 * }
 *
 * `onAction` 은 **그림 위 버튼 넷을 켜는 스위치**다 — 주지 않으면 Provider 가 있어도 막대를 그리지 않는다
 * (2026-09-10). 그림 조작은 닫되 붙여넣기 판정(`onPasteImage`)은 받아야 하는 화면이 있다 — 강사 계정의
 * 문제 v2 가 그렇다(utils/roles.js FIGURE_EDIT_ROLES). 그때 Delete 키는 종전대로 편집기가 노드를 뺀다
 * (`onAction` 이 없으면 「호스트가 맡지 않았다」와 같다). 크기 조절은 이 스위치와 무관하게 남는다.
 *
 * `onPasteImage` 는 **이미지가 편집기에 들어오려는 순간**을 호스트가 받는다(PasteFigurePlugin).
 * 주면 그 화면에서 붙여넣기·드롭·툴바 삽입이 전부 호스트를 거치고, 안 주면 종전대로 바로 꽂힌다.
 * 여기 두는 이유는 `onAction` 과 같다 — 켤 화면만 Provider 로 감싸므로 공용 편집기의 다른
 * 소비자 13곳(v1 유물 6곳·인쇄 미리보기 포함)이 영향을 받지 않는다.
 *
 * `precedingSrcs` 는 **삽입 지점 앞의 image 노드 src 목록**이고 `null` 이면 「모른다」다.
 * 호스트가 이것으로 서버 순번(`at`)을 센다 — 커서 앞의 그림 중 서버가 아는 것만 세야 하는데
 * 그 판정에 필요한 그림 목록을 쥔 쪽이 호스트다.
 * action 은 'edit'(편집 — 좌표·식을 고친다) | 'reconvert'(AI 요청) |
 * 'replace'(이미지 교체 — 원본 사진·지난 판) | 'delete'(지우기).
 * 네 가지 모두 **호출부가 서버에 보내는 일**이고 편집기는 노드를 건드리지 않는다.
 * 'delete' 도 2026-08-29 회신(§2 Q8)부터 그렇다 — 종전에는 이것만 예외로 편집기 안에서 노드를
 * 지웠는데, 그러면 서버가 알 수 없어 그림 이력이 어긋난다. 이제 `DELETE …/figures/{field}/{index}` 다.
 * value 가 null 이면 버튼을 그리지 않는다.
 *
 * ── 반환값: 「내가 맡았는가」 ──
 * `onAction` 이 **`false` 를 돌려주면 편집기가 종전대로 노드를 지운다.** 그 밖의 값(undefined 포함)은
 * 「호스트가 맡았다」로 읽어 편집기가 아무것도 하지 않는다 — 값을 안 돌려주던 옛 호출부와 같은 뜻이다.
 *
 * 왜 갈래가 필요한가: Q8 이 금지한 것은 「**서버가 세고 있는** 그림을 편집기에서 지우고 본문 PATCH 로
 * 내보내는 것」이다. 방금 붙여넣어 **서버가 아직 모르는** 그림은 그 위험이 없다. 그런데도 전부 막으면
 * 잘못 붙인 그림을 뺄 길이 「먼저 [저장]하라」뿐이 되고, 그 말을 따르면 **있지도 않았어야 할 그림이
 * 서버 본문에 커밋되고 삭제 판(source 700) 이력까지 남는다** — 화면이 시키는 대로 했더니 더 나빠진다.
 * 그래서 판정을 호스트에 맡긴다: 호스트만 **저장본**을 쥐고 있어 「서버가 아는 그림인가」를 답할 수 있다
 * (→ figureMatch.isSavedFigureSrc).
 */
/**
 * @typedef {Object} FigureEditValue
 * @property {'body'|'solution'} field
 * @property {boolean=} svgEditorEnabled
 * @property {((a: {action: string, field: string, index: number, src?: string, altText?: string, nodeKey?: string}) => boolean|void)=} onAction
 * @property {((a: {field?: string, src?: string, altText?: string, precedingSrcs: (string|null)[]|null}) => void)=} onPasteImage
 */

/** @type {import('react').Context<FigureEditValue|null>} */
const FigureEditContext = createContext(null);

/**
 * 이 편집기에 그림 편집 스위치가 켜져 있는가. Provider 가 없으면 null 이다.
 * **타입을 적어 두는 이유**: 이 파일은 JS 인데 소비자에 TS 가 섞여 있어(ImageComponent ·
 * PasteFigurePlugin), 반환을 적지 않으면 그쪽에서 `null` 로 좁혀져 프로퍼티 접근이 전부 막힌다.
 * @returns {FigureEditValue|null}
 */
export function useFigureEdit() {
  return useContext(FigureEditContext);
}

export default FigureEditContext;
